<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import AppDataTable from "@/components/AppDataTable.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import { useResourceList } from "@/composables/useResourceList";
import { fetchCampaignPages, deleteCampaignPage } from "./api";

const router = useRouter();
const columns = [
  { key: "title",   label: "Title",   sortable: true },
  { key: "actions", label: "Actions", align: "end", width: "220px" },
];
const rawItems = ref([]);
const { rows, loading, error, pendingDelete, deleting, load, askDelete, confirmDelete } =
  useResourceList({
    fetch: () => fetchCampaignPages().then((data) => { rawItems.value = Array.isArray(data) ? data : []; return data; }),
    remove: (id, row) => deleteCampaignPage(id, row?.imageUrl),
    mapRow: (p) => ({ id: p._id, title: p.message?.title ?? p.title ?? "—", imageUrl: p.message?.imageUrl ?? p.imageUrl }),
  });

const viewDialog = ref(false);
const viewItem = ref(null);
function openView(row) {
  viewItem.value = rawItems.value.find((p) => p._id === row.id) ?? null;
  viewDialog.value = true;
}
function openExternalUrl(url) {
  if (!url) return;
  window.open(url.startsWith("http") ? url : "https://" + url, "_blank");
}
</script>

<template>
  <section>
    <div class="d-flex align-center justify-space-between mb-4 gap-3 flex-wrap">
      <h1 class="text-h5">Campaign Pages</h1>
      <div class="d-flex gap-2">
        <VBtn variant="tonal" @click="load">Refresh</VBtn>
        <VBtn @click="router.push({ name: 'campaign-pages-new' })">Add Page</VBtn>
      </div>
    </div>
    <VAlert v-if="error" type="error" closable class="mb-4" @click:close="error = ''">{{ error }}</VAlert>
    <AppDataTable :columns="columns" :rows="rows" :loading="loading" row-key="id" searchable search-placeholder="Search…" empty-text="No campaign pages yet.">
      <template #cell-actions="{ row }">
        <VBtn size="small" variant="text" @click.stop="openView(row)">View</VBtn>
        <VBtn size="small" variant="text" @click.stop="router.push({ name: 'campaign-pages-edit', params: { id: row.id } })">Edit</VBtn>
        <VBtn size="small" color="error" variant="text" @click.stop="askDelete(row)">Delete</VBtn>
      </template>
    </AppDataTable>
    <ConfirmDialog :model-value="pendingDelete !== null" title="Delete campaign page?" :message="`Delete &quot;${pendingDelete?.title || 'this page'}&quot;?`" :loading="deleting" @update:model-value="pendingDelete = null" @confirm="confirmDelete" />

    <VDialog v-model="viewDialog" max-width="440" scrollable>
      <VCard>
        <VCardTitle class="pt-4 px-4">{{ viewItem?.message?.title ?? viewItem?.title ?? 'Preview' }}</VCardTitle>
        <VCardText>
          <div v-if="!viewItem" class="text-disabled pa-4 text-center">No data available.</div>
          <div v-else>
            <VImg v-if="viewItem.message?.imageUrl ?? viewItem.imageUrl" :src="viewItem.message?.imageUrl ?? viewItem.imageUrl" class="mb-3 rounded" />
            <div class="text-body-2" v-html="viewItem.message?.bodyMessage ?? viewItem.content ?? ''"></div>
            <VBtn v-if="viewItem.message?.redirectUrl ?? viewItem.redirectUrl" variant="tonal" size="small" class="mt-3"
              @click="openExternalUrl(viewItem.message?.redirectUrl ?? viewItem.redirectUrl)">
              Open Link
            </VBtn>
          </div>
        </VCardText>
        <VCardActions class="px-4 pb-4 justify-end">
          <VBtn @click="viewDialog = false">Close</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>
